import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertTriangle, Shield, TrendingUp, TrendingDown, Bell, CheckCircle2,
  Users, ArrowRight, Loader2, ChevronLeft, FileText, Brain,
  Send, Clock, Eye, XCircle, Minus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function getScoreColor(score, prefix = 'bg') {
  if (score <= 3) return `${prefix}-emerald-500`;
  if (score <= 5) return `${prefix}-yellow-500`;
  if (score <= 7) return `${prefix}-orange-500`;
  return `${prefix}-red-500`;
}

function getScoreTextColor(score) {
  if (score <= 3) return 'text-emerald-400';
  if (score <= 5) return 'text-yellow-400';
  if (score <= 7) return 'text-orange-400';
  return 'text-red-400';
}

function RiskBar({ score, label, category }) {
  const pct = ((score || 1) / 10) * 100;
  const color = getScoreColor(score);
  const textColor = getScoreTextColor(score);
  return (
    <div data-testid={`risk-bar-${category}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-slate-300">{label}</span>
        <span className={`text-sm font-bold ${textColor}`}>{score}/10</span>
      </div>
      <div className="w-full bg-slate-700/50 rounded-full h-2.5">
        <div className={`h-2.5 rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function LevelBadge({ level }) {
  const styles = {
    low: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    moderate: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    elevated: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    high: 'bg-red-500/20 text-red-300 border-red-500/30',
    critical: 'bg-red-900/40 text-red-200 border-red-500/50',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${styles[level] || styles.low}`}>
      {level?.toUpperCase()}
    </span>
  );
}

function ParticipantCard({ p, onClick }) {
  const scores = p.scores || {};
  const catOrder = ['violence', 'instability', 'relapse', 'recidivism'];
  return (
    <Card
      className="bg-slate-800/60 border-slate-700/50 hover:border-blue-500/40 cursor-pointer transition-all"
      onClick={onClick}
      data-testid={`participant-card-${p.participant_id}`}
    >
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-white font-medium">{p.participant_name}</p>
            <p className="text-xs text-slate-400">Composite: {p.composite_score}/10</p>
          </div>
          <LevelBadge level={p.overall_level} />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {catOrder.map(cat => {
            const s = scores[cat] || {};
            const score = s.score || 1;
            const color = getScoreTextColor(score);
            return (
              <div key={cat} className="text-center">
                <p className={`text-sm font-bold ${color}`}>{score}</p>
                <p className="text-[10px] text-slate-500">{cat.slice(0, 4).toUpperCase()}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function AlertCard({ alert, onAcknowledge, onResolve, canResolve }) {
  const urgencyColors = {
    medium: 'border-yellow-500/40 bg-yellow-900/10',
    high: 'border-orange-500/40 bg-orange-900/10',
    critical: 'border-red-500/50 bg-red-900/20',
  };
  return (
    <div className={`rounded-lg border p-3 ${urgencyColors[alert.urgency] || urgencyColors.medium}`} data-testid={`alert-${alert.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className={`h-4 w-4 ${alert.urgency === 'critical' ? 'text-red-400' : 'text-orange-400'}`} />
            <span className="text-white text-sm font-medium">{alert.participant_name}</span>
            <LevelBadge level={alert.level} />
          </div>
          <p className="text-xs text-slate-300">{alert.message}</p>
          <p className="text-[10px] text-slate-500 mt-1">{new Date(alert.created_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-1 shrink-0">
          {!alert.acknowledged_by && (
            <Button size="sm" variant="ghost" className="text-yellow-400 hover:text-yellow-300 h-7 px-2" onClick={() => onAcknowledge(alert.id)}>
              <Eye className="h-3 w-3" />
            </Button>
          )}
          {canResolve && alert.status === 'active' && (
            <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300 h-7 px-2" onClick={() => onResolve(alert.id)}>
              <CheckCircle2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RiskEnginePage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { participantId } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [profile, setProfile] = useState(null);
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [pendingSummaries, setPendingSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjusting, setAdjusting] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ adjustment: 0, reason: '' });
  const [creatingSummary, setCreatingSummary] = useState(false);
  const [overrideForm, setOverrideForm] = useState(null);
  const isAdmin = ['coordinator', 'admin'].includes(user?.role);

  const headers = useMemo(() => ({ 'Content-Type': 'application/json' }), []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, alertRes] = await Promise.all([
        fetch(`${API}/api/risk-engine/dashboard`, { headers, credentials: 'include' }),
        fetch(`${API}/api/risk-engine/alerts`, { headers, credentials: 'include' }),
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (alertRes.ok) {
        const alertData = await alertRes.json();
        setAlerts(alertData.alerts || []);
      }
      if (isAdmin) {
        const sumRes = await fetch(`${API}/api/risk-engine/pending-summaries`, { headers, credentials: 'include' });
        if (sumRes.ok) setPendingSummaries((await sumRes.json()).summaries || []);
      }
    } catch { toast.error('Failed to load dashboard'); }
    setLoading(false);
  }, [isAdmin, headers]);

  const loadParticipantProfile = useCallback(async (pid) => {
    setLoading(true);
    try {
      const [profRes, evtRes] = await Promise.all([
        fetch(`${API}/api/risk-engine/participant/${pid}`, { headers, credentials: 'include' }),
        fetch(`${API}/api/risk-engine/events/${pid}?limit=30`, { headers, credentials: 'include' }),
      ]);
      if (profRes.ok) setProfile(await profRes.json());
      if (evtRes.ok) setEvents((await evtRes.json()).events || []);
    } catch { toast.error('Failed to load participant profile'); }
    setLoading(false);
  }, [headers]);

  useEffect(() => {
    if (participantId) {
      loadParticipantProfile(participantId);
    } else {
      loadDashboard();
    }
  }, [participantId, loadParticipantProfile, loadDashboard]);

  const acknowledgeAlert = async (alertId) => {
    await fetch(`${API}/api/risk-engine/alerts/${alertId}/acknowledge`, { method: 'PUT', headers, credentials: 'include' });
    loadDashboard();
  };

  const resolveAlert = async (alertId) => {
    await fetch(`${API}/api/risk-engine/alerts/${alertId}/resolve`, { method: 'PUT', headers, credentials: 'include' });
    loadDashboard();
  };

  const submitAdjustment = async (category) => {
    if (!adjustForm.reason) return;
    await fetch(`${API}/api/risk-engine/adjust`, { method: 'POST', headers, credentials: 'include',
      body: JSON.stringify({ participant_id: participantId, category, adjustment: parseFloat(adjustForm.adjustment), reason: adjustForm.reason }),
    });
    setAdjusting(null);
    setAdjustForm({ adjustment: 0, reason: '' });
    loadParticipantProfile(participantId);
  };

  const submitHardOverride = async () => {
    if (!overrideForm) return;
    await fetch(`${API}/api/risk-engine/hard-override`, { method: 'POST', headers, credentials: 'include',
      body: JSON.stringify(overrideForm),
    });
    setOverrideForm(null);
    loadParticipantProfile(participantId);
  };

  const createSupportSummary = async () => {
    setCreatingSummary(true);
    await fetch(`${API}/api/risk-engine/support-summary`, { method: 'POST', headers, credentials: 'include',
      body: JSON.stringify({ participant_id: participantId }),
    });
    setCreatingSummary(false);
    loadParticipantProfile(participantId);
  };

  const approveSummary = async (summaryId, action) => {
    await fetch(`${API}/api/risk-engine/support-summaries/${summaryId}/approve`, { method: 'PUT', headers, credentials: 'include',
      body: JSON.stringify({ action }),
    });
    loadDashboard();
  };

  const shareSummary = async (summaryId) => {
    await fetch(`${API}/api/risk-engine/support-summaries/${summaryId}/share`, { method: 'PUT', headers, credentials: 'include' });
    loadParticipantProfile(participantId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  // Participant detail view
  if (participantId && profile) {
    const catOrder = ['violence', 'instability', 'relapse', 'recidivism'];
    const catLabels = { violence: 'Violence', instability: 'Instability', relapse: 'Relapse', recidivism: 'Recidivism' };
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="text-slate-400" onClick={() => navigate('/risk-engine')}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Dashboard
          </Button>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="participant-name">{profile.participant_name}</h1>
            <p className="text-slate-400 text-sm">Real-Time Risk Profile</p>
          </div>
          <div className="flex items-center gap-3">
            <LevelBadge level={profile.overall_level} />
            <span className="text-white font-bold text-lg">{profile.highest_score}/10</span>
          </div>
        </div>

        {/* Risk Scores */}
        <Card className="bg-slate-800/60 border-slate-700/50">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Shield className="h-4 w-4 text-blue-400" /> Risk Scores (1-10)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {catOrder.map(cat => {
              const data = profile.risk_scores?.[cat] || {};
              return (
                <div key={cat}>
                  <RiskBar score={data.score || 1} label={catLabels[cat]} category={cat} />
                  {data.baseline && (
                    <p className="text-[10px] text-slate-500 mt-0.5">Baseline: {data.baseline}/10</p>
                  )}
                  {/* Adjust button for admin */}
                  {isAdmin && (
                    <div className="mt-1">
                      {adjusting === cat ? (
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="number" step="0.5" min="-5" max="5"
                            value={adjustForm.adjustment}
                            onChange={e => setAdjustForm(p => ({ ...p, adjustment: e.target.value }))}
                            className="w-20 px-2 py-1 rounded bg-slate-900 border border-slate-600 text-white text-sm"
                          />
                          <input
                            type="text" placeholder="Reason"
                            value={adjustForm.reason}
                            onChange={e => setAdjustForm(p => ({ ...p, reason: e.target.value }))}
                            className="flex-1 px-2 py-1 rounded bg-slate-900 border border-slate-600 text-white text-sm"
                          />
                          <Button size="sm" className="bg-blue-600 h-7" onClick={() => submitAdjustment(cat)}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => setAdjusting(null)}>
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-slate-500 h-6 px-2 text-[10px]" onClick={() => setAdjusting(cat)}>
                          Adjust Score
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Stage of Change */}
        {profile.stage_of_change?.stage && (
          <Card className="bg-slate-800/60 border-slate-700/50">
            <CardContent className="pt-4 pb-4 flex items-center gap-3">
              <Brain className="h-5 w-5 text-purple-400" />
              <div>
                <span className="text-slate-400 text-sm">Stage of Change:</span>
                <span className="text-white font-medium ml-2">{profile.stage_of_change.label}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hard Override (Admin only) */}
        {isAdmin && (
          <Card className="bg-red-900/10 border-red-500/30">
            <CardHeader><CardTitle className="text-red-400 text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Hard Override</CardTitle></CardHeader>
            <CardContent>
              {!overrideForm ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                  data-testid="hard-override-btn"
                  onClick={() => setOverrideForm({ participant_id: participantId, category: 'violence', trigger: 'credible_violence_threat', notes: '' })}
                >
                  Initiate Hard Override
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <select value={overrideForm.category} onChange={e => setOverrideForm(p => ({ ...p, category: e.target.value }))} className="px-2 py-1.5 rounded bg-slate-900 border border-slate-600 text-white text-sm">
                      {catOrder.map(c => <option key={c} value={c}>{catLabels[c]}</option>)}
                    </select>
                    <select value={overrideForm.trigger} onChange={e => setOverrideForm(p => ({ ...p, trigger: e.target.value }))} className="px-2 py-1.5 rounded bg-slate-900 border border-slate-600 text-white text-sm">
                      <option value="credible_violence_threat">Credible Violence Threat</option>
                      <option value="repeated_drug_use">Repeated Drug Use</option>
                      <option value="absconding_housing">Absconding from Housing</option>
                      <option value="severe_instability_breakdown">Severe Instability/Breakdown</option>
                    </select>
                  </div>
                  <input type="text" placeholder="Notes..." value={overrideForm.notes} onChange={e => setOverrideForm(p => ({ ...p, notes: e.target.value }))} className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-600 text-white text-sm" />
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={submitHardOverride}>Confirm Override (sets to 8+)</Button>
                    <Button size="sm" variant="ghost" onClick={() => setOverrideForm(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Active Alerts */}
        {profile.active_alerts?.length > 0 && (
          <Card className="bg-slate-800/60 border-slate-700/50">
            <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Bell className="h-4 w-4 text-yellow-400" /> Active Alerts</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {profile.active_alerts.map(a => (
                <AlertCard key={a.id} alert={a} onAcknowledge={acknowledgeAlert} onResolve={resolveAlert} canResolve={isAdmin} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Support Summary */}
        <Card className="bg-slate-800/60 border-slate-700/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base flex items-center gap-2"><FileText className="h-4 w-4 text-blue-400" /> Support Summaries</CardTitle>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={createSupportSummary} disabled={creatingSummary} data-testid="create-summary-btn">
                {creatingSummary ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Brain className="h-3 w-3 mr-1" />}
                Generate AI Summary
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <SummaryList participantId={participantId} headers={headers} isAdmin={isAdmin} onShare={shareSummary} />
          </CardContent>
        </Card>

        {/* Event History */}
        <Card className="bg-slate-800/60 border-slate-700/50">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> Risk Event History</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {events.length === 0 && <p className="text-slate-500 text-sm text-center py-4">No risk events recorded yet</p>}
              {events.map(e => (
                <div key={e.id} className="flex items-center gap-3 text-sm border-b border-slate-700/30 pb-2">
                  {e.direction === 'increase' ? (
                    <TrendingUp className="h-4 w-4 text-red-400 shrink-0" />
                  ) : e.direction === 'decrease' ? (
                    <TrendingDown className="h-4 w-4 text-emerald-400 shrink-0" />
                  ) : (
                    <Minus className="h-4 w-4 text-slate-400 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-300 truncate">{e.category}: {e.old_score} → {e.new_score} ({e.change > 0 ? '+' : ''}{e.change})</p>
                    <p className="text-xs text-slate-500 truncate">{e.reason}</p>
                  </div>
                  <span className="text-[10px] text-slate-500 shrink-0">{new Date(e.timestamp).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard view
  const participants = dashboard?.participants || [];
  const summary = dashboard?.summary || {};

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="risk-engine-title">Real-Time Risk Engine</h1>
            <p className="text-slate-400 text-sm">Dynamic risk monitoring across all participants</p>
          </div>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/core8')} data-testid="new-assessment-btn">
          <FileText className="h-4 w-4 mr-2" /> New CORE-8 Assessment
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Critical', count: summary.critical || 0, color: 'text-red-400 bg-red-900/20 border-red-500/30' },
          { label: 'High', count: summary.high || 0, color: 'text-orange-400 bg-orange-900/20 border-orange-500/30' },
          { label: 'Elevated', count: summary.elevated || 0, color: 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30' },
          { label: 'Moderate', count: summary.moderate || 0, color: 'text-blue-400 bg-blue-900/20 border-blue-500/30' },
          { label: 'Low', count: summary.low || 0, color: 'text-emerald-400 bg-emerald-900/20 border-emerald-500/30' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border p-3 text-center ${s.color}`} data-testid={`summary-${s.label.toLowerCase()}`}>
            <p className="text-2xl font-bold">{s.count}</p>
            <p className="text-xs opacity-80">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="participants" className="space-y-4">
        <TabsList className="bg-slate-800/60 border border-slate-700/50">
          <TabsTrigger value="participants" className="data-[state=active]:bg-slate-700">
            <Users className="h-4 w-4 mr-1" /> Participants ({participants.length})
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-slate-700">
            <Bell className="h-4 w-4 mr-1" /> Alerts ({alerts.length})
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="summaries" className="data-[state=active]:bg-slate-700">
              <FileText className="h-4 w-4 mr-1" /> Pending Summaries ({pendingSummaries.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="participants">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {participants.map(p => (
              <ParticipantCard key={p.participant_id} p={p} onClick={() => navigate(`/risk-engine/${p.participant_id}`)} />
            ))}
            {participants.length === 0 && (
              <p className="text-slate-500 text-center py-8 col-span-2">No participants with risk scores yet. Run a CORE-8 assessment to get started.</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="alerts">
          <div className="space-y-2">
            {alerts.length === 0 && <p className="text-slate-500 text-center py-8">No active alerts</p>}
            {alerts.map(a => (
              <AlertCard key={a.id} alert={a} onAcknowledge={acknowledgeAlert} onResolve={resolveAlert} canResolve={isAdmin} />
            ))}
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="summaries">
            <div className="space-y-3">
              {pendingSummaries.length === 0 && <p className="text-slate-500 text-center py-8">No pending summaries</p>}
              {pendingSummaries.map(s => (
                <Card key={s.id} className="bg-slate-800/60 border-slate-700/50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-white font-medium">{s.participant_name}</p>
                        <p className="text-sm text-slate-300 mt-1 italic">"{s.summary_text}"</p>
                        <p className="text-xs text-slate-500 mt-1">By {s.created_by_name} on {new Date(s.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7" onClick={() => approveSummary(s.id, 'approved')}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => approveSummary(s.id, 'rejected')}>
                          <XCircle className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function SummaryList({ participantId, headers, isAdmin, onShare }) {
  const [summaries, setSummaries] = useState([]);

  useEffect(() => {
    loadSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId]);

  const loadSummaries = async () => {
    try {
      const res = await fetch(`${API}/api/risk-engine/support-summaries/${participantId}`, { headers, credentials: 'include' });
      if (res.ok) setSummaries((await res.json()).summaries || []);
    } catch (e) { console.error(e); }
  };

  if (summaries.length === 0) return <p className="text-slate-500 text-sm text-center py-4">No support summaries yet</p>;

  return (
    <div className="space-y-3">
      {summaries.map(s => (
        <div key={s.id} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/30">
          <p className="text-sm text-slate-300 italic">"{s.summary_text}"</p>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={
                s.approval_status === 'approved' ? 'border-emerald-500/40 text-emerald-300' :
                s.approval_status === 'rejected' ? 'border-red-500/40 text-red-300' :
                'border-yellow-500/40 text-yellow-300'
              }>{s.approval_status}</Badge>
              {s.shared_with_client && <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">Shared</Badge>}
            </div>
            {isAdmin && s.approval_status === 'approved' && !s.shared_with_client && (
              <Button size="sm" variant="ghost" className="text-blue-400 h-7 text-xs" onClick={() => onShare(s.id)}>
                <Send className="h-3 w-3 mr-1" /> Share with Client
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
